// English dictionary: every key of ru.ts, no more, no fewer (the type below makes the compiler check both).
// Proper names follow docs/glossary-en.md. Plural forms: `{n|one|other}`.
import type { RU } from './ru.js';

export const EN: Record<keyof typeof RU, string> = {
  /* ---------------- classes, roles ---------------- */
  'class.knight': 'Knight',
  'class.alchemist': 'Alchemist',
  'class.reaper': 'Reaper',
  'class.doctor': 'Doctor',
  'class.outsider': 'Astral',
  'class.summoner': 'Summoners',
  'role.guard': 'Guardian',
  'role.shooter': 'Marksman',
  'role.duelist': 'Duellist',
  'role.support': 'Support',
  'role.gamechanger': 'Tide-turner',
  'role.summoner': 'Summoner',

  /* ---------------- heroes ---------------- */
  'hero.rhino': 'Rhino',
  'hero.durer': "Dürer's Knight",
  'hero.landsknecht': 'Landsknecht',
  'hero.arquebusier': 'Arquebusier',
  'hero.astrologer': 'Astrologer',
  'hero.salamander': 'Salamander',
  'hero.death': 'Death',
  'hero.ferryman': 'Ferryman',
  'hero.devil': 'Duellist Devil',
  'hero.plague': 'Plague Doctor',
  'hero.monk': 'Monk Physician',
  'hero.grotesque': 'Grotesque',
  'hero.shadow': 'Shadow',
  'hero.sin': 'Sin',
  'hero.nightmare': 'Nightmare',
  'hero.birdcatcher': 'Birdcatcher',
  'hero.puppeteer': 'Puppeteer',
  'hero.shewolf': 'She-Wolf',

  /* ---------------- skills: names ---------------- */
  'skill.rhino.Q': 'Battering Ram',
  'skill.rhino.W': 'Brace',
  'skill.durer.Q': 'Challenge',
  'skill.durer.W': 'Pavise',
  'skill.landsknecht.Q': 'Sweep',
  'skill.landsknecht.W': 'War Cry',
  'skill.arquebusier.Q': 'Aimed Shot',
  'skill.arquebusier.W': 'Fall Back',
  'skill.astrologer.Q': 'Star Volley',
  'skill.astrologer.W': 'Divination',
  'skill.salamander.Q': 'Jet of Flame',
  'skill.salamander.W': 'Burning Trail',
  'skill.death.Q': 'Lunge',
  'skill.death.W': 'Feint',
  'skill.ferryman.Q': 'Hook',
  'skill.ferryman.W': 'Lantern of Souls',
  'skill.devil.Q': 'Leap',
  'skill.devil.W': 'Pact',
  'skill.plague.Q': 'Phial',
  'skill.plague.W': 'Ward',
  'skill.monk.Q': 'Prayer',
  'skill.monk.W': 'Holy Circle',
  'skill.grotesque.Q': 'Plague Cloud',
  'skill.grotesque.W': 'Feather Sweep',
  'skill.shadow.Q': 'Meld',
  'skill.shadow.W': 'Shadow on the Wall',
  'skill.sin.Q': 'Temptation',
  'skill.sin.W': 'Atonement',
  'skill.nightmare.Q': 'Flock',
  'skill.nightmare.W': 'Delusion',
  'skill.birdcatcher.Q': 'Mischief of Magpies',
  'skill.birdcatcher.W': 'Falcon on the Glove',
  'skill.puppeteer.Q': 'Clay Golem',
  'skill.puppeteer.W': 'Binding',
  'skill.shewolf.Q': 'Pack',
  'skill.shewolf.W': 'Turn Wolf',

  /* ---------------- skills: descriptions ---------------- */
  'skill.rhino.Q.desc': 'Charges up to {range} m: the first foe met takes {dmg} damage, is thrown back and stunned for {stun} s; if a wall stands behind him, the stun lasts +{wallStun} s more.',
  'skill.rhino.W.desc': 'Braces for {dur} s: blows from the front ({arc} arc) are fully blocked, speed −{slow}, heals {heal} of max health per second. If the stance blocked anything, his next attack stuns for {stun} s.',
  'skill.durer.Q.desc': 'Foes within {r} m are forced to attack only him for {taunt} s; their casts and charges are broken.',
  'skill.durer.W.desc': 'Plants a shield wall {width} m wide before him for {life} s — it stops enemy missiles.',
  'skill.landsknecht.Q.desc': 'A half-circle blow ({arc}) of {range} m: {dmg} damage to each foe and a knockback.',
  'skill.landsknecht.W.desc': 'He and allies within {r} m gain +{haste} speed for {life} s.',
  'skill.arquebusier.Q.desc': 'Hold to charge (up to {charge} s; −{slow} speed while charging). Damage {dmgMin}–{dmgMax} and range {rangeMin}–{rangeMax} m grow with the charge.',
  'skill.arquebusier.W.desc': 'Leaps {range} m back, leaving a trap where he stood (arms in {arm} s, lasts {life} s). A foe who steps on it takes {dmg} damage, a {slow} slow for {slowFor} s and is revealed for 2 s.',
  'skill.astrologer.Q.desc': 'A fan of three stars reaching {range} m, each dealing {dmg} damage.',
  'skill.astrologer.W.desc': 'A spot up to {range} m away: a circle of {r} m radius lifts fog and grass for {life} s; foes inside lose {drain} of max health per second.',
  'skill.salamander.Q.desc': 'A jet of flame {range} m long in a {arc} cone for {life} s, {dps} damage per second; the jet can follow its target while the Salamander herself walks {slow} slower.',
  'skill.salamander.W.desc': 'Leaps {range} m back, leaving a burning pool of {r} m radius where she stood — {dps} damage per second to foes for {life} s.',
  'skill.death.Q.desc': 'Dashes {range} m through foes: each one in the path takes {dmg} damage. A hit resets the cooldown at once (no more than once per {window} s).',
  'skill.death.W.desc': 'A {range} m step and {invuln} s of invulnerability. If struck during it, the next Lunge deals ×{mult} damage and costs no cooldown.',
  'skill.ferryman.Q.desc': 'A hook thrown {range} m: the first foe takes {dmg} damage, is stunned for {stun} s and dragged close.',
  'skill.ferryman.W.desc': 'A lantern of {r} m radius around him for {life} s: foes inside are slowed by {slow} and revealed.',
  'skill.devil.Q.desc': 'Leaps to a spot up to {range} m away: on landing, foes within {r} m take {dmg} damage and a {slow} slow for {slowFor} s.',
  'skill.devil.W.desc': 'Gives up {cost} of base health (never fatal — at least 1 remains) and deals +{bonus} damage for {life} s.',
  'skill.plague.Q.desc': 'A phial thrown up to {range} m: a pool of {r} m radius for {life} s heals allies {heal} per second, while foes take {dmg} damage per second and a {slow} slow.',
  'skill.plague.W.desc': 'A shield against {shield} damage on an ally (up to {range} m) or himself for {life} s; clears slows. If the shield is not broken, the cooldown is {refund} shorter.',
  'skill.monk.Q.desc': 'An ally (up to {range} m) or he himself restores {total} health over {life} s.',
  'skill.monk.W.desc': 'A circle of {r} m radius for {life} s: allies inside cannot be stunned, slowed, knocked back, charmed or frightened.',
  'skill.grotesque.Q.desc': 'A poison cloud of {r} m radius creeps forward ({speed} m/s) for {life} s, {dps} damage per second to foes inside.',
  'skill.grotesque.W.desc': 'A sweep: foes within {r} m take {dmg} damage and are thrown back.',
  'skill.shadow.Q.desc': 'Only in grass or out of enemy sight: for up to {life} s she is unseen by the enemy — even up close and under reveals. Leaving cover ends it. Attacking from hiding breaks it, and {hits} {hits|blow|blows} in a row (within {window} s) deal ×{mult} damage. While unseen she restores a further +{regen}% of max health per second.',
  'skill.shadow.W.desc': 'Casts her shadow to a spot up to {range} m away; after {delay} s she trades places with it. Every enemy within {r} m of where she vanished and of where she appears takes {dmg} damage (once, if caught in both).',
  'skill.sin.Q.desc': 'A foe within {range} m walks to Sin of his own accord for {life} s: he ignores orders and can neither attack nor use skills or artifacts. All the while Sin draws {drain} of his max health per second and heals himself as much.',
  'skill.sin.W.desc': 'For {life} s allies within {r} m of Sin take only {taken} of their damage, while Sin himself takes just {self} of the blow — the rest burns away (his shield and defences work as usual).',
  'skill.nightmare.Q.desc': 'Flies as a flock of birds to a spot up to {range} m away, invulnerable in flight. Foes within {r} m of the landing flee in terror to their base for {fear} s, ignoring orders.',
  'skill.nightmare.W.desc': 'For {life} s foes within {r} m of him mistake friend for foe: a basic attack has a {chance} chance to strike their nearest ally in attack range for {mult} damage.',
  'skill.birdcatcher.Q.desc': 'Looses {count} {count|magpie|magpies} for {life} s ({hp} health each): they fly over walls and peck his target or the nearest foe for {dmg} damage. Each peck delays a skill the foe has begun by {delay} s.',
  'skill.birdcatcher.W.desc': 'The falcon flies at a foe within {range} m ({speed} m/s) and strikes: {dmg} damage and a {slow} slow for {slowFor} s.',
  'skill.puppeteer.Q.desc': 'Moulds a golem at a spot up to {range} m away for {life} s: {hp} health; it strikes the nearest foe, creature or beast within {aggro} m for {dmg} damage, and with none about it walks to the nearest point not ours and holds it: it contests the point but never takes it. One golem at a time: a new one replaces the old.',
  'skill.puppeteer.W.desc': 'A throw at a spot up to {range} m away: every foe in the {r} m circle is bound for {life} s to the nearest thing in that circle — another foe (to each other), a creature, a beast, a tree or a wall. The bound may draw closer, but never further apart than they already came. Interrupts casts and dashes. Nothing to bind — a miss, the cooldown runs all the same.',
  'skill.shewolf.Q.desc': 'Calls {count} {count|wolf|wolves} for {life} s ({hp} health each): they bite her target for {dmg} damage, circling to its flanks.',
  'skill.shewolf.W.desc': 'Turns wolf for {life} s: +{haste} speed, attacks up close ({range} m) and deals +{bonus} damage.',

  /* ---------------- passives ---------------- */
  'passive.rhino': 'Thick Hide',
  'passive.durer': 'Protector',
  'passive.landsknecht': 'Zweihänder',
  'passive.arquebusier': 'Musket Rest',
  'passive.astrologer': 'Stargazer',
  'passive.salamander': 'Clinging Flame',
  'passive.death': 'Memento Mori',
  'passive.ferryman': 'Heavy Hand',
  'passive.devil': 'Blood Debt',
  'passive.plague': 'Triage',
  'passive.monk': 'Humility',
  'passive.grotesque': 'Foulness',
  'passive.shadow': 'No Reflection',
  'passive.sin': 'Original Sin',
  'passive.nightmare': 'Sleeplessness',
  'passive.birdcatcher': 'Keen Eye',
  'passive.puppeteer': 'Master',
  'passive.shewolf': 'Pack Leader',
  'passive.rhino.desc': 'Knockbacks and pulls move him only {mult} of the distance.',
  'passive.durer.desc': 'Allies within {r} m of him take {reduce} less damage (before shields). Does not work on himself.',
  'passive.landsknecht.desc': 'His basic attack also strikes one more nearest foe in a {arc} arc before him — for {share} of the attack damage.',
  'passive.arquebusier.desc': 'After standing still for {still} s, his basic attack reaches {bonus} m further; the bonus is lost the moment he moves.',
  'passive.astrologer.desc': 'Any enemy hero touched by his damage is revealed to his whole team for {reveal} s — even in grass and fog.',
  'passive.salamander.desc': 'A foe touched by her fire (jet or pool) burns on for {burn} s — {dps} damage per second.',
  'passive.death.desc': '+{bonus} damage against enemy heroes below {below} health.',
  'passive.ferryman.desc': 'Basic attacks slow the target by {slow} for {slowFor} s.',
  'passive.devil.desc': 'Heals for {leech} of the damage dealt to enemy heroes (damage to beasts does not heal).',
  'passive.plague.desc': 'The Phial’s healing and the Ward’s shield are {bonus} stronger on an ally below {below} health.',
  'passive.monk.desc': 'Stuns, slows, charm and fear on him last {cut} shorter; he restores a further +{regen}% of max health per second on top of the usual {base}%.',
  'passive.grotesque.desc': 'A melee hero who strikes him with a basic attack takes {dmg} damage in return.',
  'passive.shadow.desc': 'Traps do not give her away, and Divination shows her only for the first {revealFor} s. After standing still for {stillFor} s she becomes a silhouette: she can be seen, but no attack, skill, zone or control touches her, and enemy orders to attack her are dropped; on a point the silhouette holds but does not capture: it contests the point and keeps the enemy from taking it back, but moves no progress itself and does not speed a partner’s capture. The silhouette fades the moment she moves, starts an attack or uses a skill, artifact or relic; while her Totem of Wrath stands she cannot become one.',
  'passive.sin.desc': 'If he wounded a foe within the last {window} s, his team’s kill of that foe earns +{bonus} more points.',
  'passive.nightmare.desc': 'Foes within {r} m of him restore no health — neither by regeneration nor by healing; the fountain heals them at half strength.',
  'passive.birdcatcher.desc': 'His magpies see {sight} m around them, over the trees.',
  'passive.puppeteer.desc': 'When his golem is smashed (not when it crumbles on its own), the Puppeteer gains a shield of {shield} of max health for {life} s.',
  'passive.shewolf.desc': 'While at least one of her wolves is within {r} m of her, she takes {reduce} less damage.',

  /* ---------------- artifacts ---------------- */
  'art.shoulder': 'Pauldron',
  'art.whetstone': 'Whetstone',
  'art.boots': "Courier's Boots",
  'art.salve': 'Healing Salve',
  'art.hourglass': 'Hourglass',
  'art.mistAmulet': 'Mist Amulet',
  'art.graveCandle': 'Grave Candle',
  'art.dragonTooth': "Dragon's Tooth",
  'art.banner': 'Banner',
  'art.bloodvial': 'Blood Vial',
  'art.windoil': 'Wind Oil',
  'art.cloak': 'Parchment Cloak',
  'art.totemWard': 'Totem of Fortitude',
  'art.totemWrath': 'Totem of Wrath',
  'art.brand': 'Brand',
  'art.tar': 'Tar Flask',
  'art.shoulder.desc': '+{hp} max health',
  'art.whetstone.desc': '+{atk}% attack speed',
  'art.boots.desc': '+{move}% move speed out of combat ({quiet} s unhurt)',
  'art.salve.desc': '+{regen}% health per second out of combat ({quiet} s unhurt)',
  'art.hourglass.desc': 'Skill and active artifact cooldowns run {rate}% faster',
  'art.mistAmulet.desc': 'An enemy basic attack on you misses with a {chance}% chance',
  'art.graveCandle.desc': 'Respawn {cut}% sooner',
  'art.dragonTooth.desc': '+{dmg}% damage to enemy heroes above {above}% health',
  'art.banner.desc': 'The point beneath you is captured {cap}% faster (two banners do not stack)',
  'art.bloodvial.desc': 'Fills {rate}% health per second, up to {max}%; drink at any moment — heals what it holds',
  'art.windoil.desc': '+{haste}% move speed for {dur} s',
  'art.cloak.desc': '{dur} s as a bare outline: cannot be targeted, neither strikes nor casts',
  'art.totemWard.desc': 'Set at your feet: for {life} s heals allies within {r} m by {pct}% health per second, breaks after {hp} blows',
  'art.totemWrath.desc': 'Set at your feet: for {life} s drains foes within {r} m of {pct}% health per second, breaks after {hp} blows',
  'art.brand.desc': 'A foe or beast within {range} m takes +{pct}% damage for {dur} s',
  'art.tar.desc': 'Thrown {range} m: a {r} m pool for {life} s, {slow}% slow',

  /* ---------------- relics ---------------- */
  'relic.martyrShield': "Martyr's Shield",
  'relic.wildHorn': 'Horn of the Wild Hunt',
  'relic.bestiaryEye': 'Eye of the Bestiary',
  'relic.hydraHeart': "Hydra's Heart",
  'relic.martyrShield.desc': 'Press (T): {dur} s of invulnerability for you and an ally within {r} m — striking and casting still allowed; cooldown {cd} s',
  'relic.wildHorn.desc': 'Always: +{move}% run speed and +{atk}% attack speed for both',
  'relic.bestiaryEye.desc': 'Press (T): for {dur} s the team sees the whole map and deals +{dmg}% damage to heroes, and its skill and artifact cooldowns run {rate}% faster — no fog, no grass, even the unseen are shown; cooldown {cd} s',
  'relic.hydraHeart.desc': 'Always: +{regen}% health per second for both, in combat too',

  /* ---------------- monsters, summoned creatures ---------------- */
  'monster.behemoth': 'Behemoth',
  'monster.griffin': 'Griffin',
  'summon.magpie': 'Magpie',
  'summon.golem': 'Golem',
  'summon.wolf': 'Wolf',

  /* ---------------- teams ---------------- */
  'team.a': 'Blood',
  'team.b': 'Ink',
  'team.a.full': 'Blood Print',
  'team.b.full': 'Ink Print',

  /* ---------------- built-in maps ---------------- */
  'map.Погорелый посад': 'Burnt Town',
  'map.Сенокос': 'Haymaking',
  'map.Мельничный брод': 'Mill Ford',
  'map.Волчья чаща': 'Wolf Thicket',
  'map.Каменный перевал': 'Stone Pass',

  /* ---------------- map validation issues (core codes, see core/map/schema.ts MapIssueCode) ---------------- */
  'mapIssue.expectedObject': 'expected an object',
  'mapIssue.requiredField': 'required field',
  'mapIssue.unknownField': 'unknown field',
  'mapIssue.expectedArray': 'expected an array',
  'mapIssue.expectedNumber': 'expected a finite number, got {got}',
  'mapIssue.mustBePositive': 'must be > 0, got {got}',
  'mapIssue.expectedPoint': 'expected a point [x, z]',
  'mapIssue.expectedOneOf': 'expected one of {options}, got {got}',
  'mapIssue.expectedBool': 'expected true or false',
  'mapIssue.pointsCoincide': 'points {i} and {j} coincide',
  'mapIssue.lineTooShort': 'a line needs at least 2 points, got {n}',
  'mapIssue.polygonTooShort': 'a polygon needs at least 3 points, got {n}',
  'mapIssue.polygonZeroArea': 'the polygon has zero area',
  'mapIssue.polygonEdgesCross': 'polygon edges {i} and {j} cross',
  'mapIssue.centerNotSymmetric': 'its mirror (-x, -z) is missing from center: centre objects must be symmetric as a set',
  'mapIssue.outsideMap': 'off the map (|x| <= {hw}, |z| <= {hh})',
  'mapIssue.baseNearEdge': 'too close to the edge: keep within |x| <= {hw}, |z| <= {hh} so the shop and flag stay on the map',
  'mapIssue.mirroredHalfPoint': 'on the mirrored half: place it on team A\'s side of the base-to-base diagonal',
  'mapIssue.noVertexInside': 'no point lies inside the map',
  'mapIssue.mirroredHalf': 'on the mirrored half: create it on team A\'s side of the base-to-base diagonal, the mirror is added on load',
  'mapIssue.mirroredHalfCentroid': 'on the mirrored half (by the centroid of its points): create it on team A\'s side of the base-to-base diagonal, the mirror is added on load',
  'mapIssue.duplicates': 'duplicates {first}',
  'mapIssue.mirrorDuplicates': 'its mirror duplicates {first}',
  'mapIssue.duplicatesMirrorOf': 'duplicates the mirror of {first}',
  'mapIssue.bridgeNotOnStream': 'not on a stream (must be within {reach} m of one)',
  'mapIssue.bridgeStreamTooWide': 'the stream beneath is wider than {span} m: a bridge cannot span it',
  'mapIssue.fordMissesStream': 'does not cross a stream: the stream\'s centre line must pass through the ford\'s rectangle',
  'mapIssue.fordTooShort': 'does not cover the stream bank to bank: needs at least {need} m across the stream',
  'mapIssue.fordTooNarrow': 'too narrow: needs at least {need} m along the stream over the whole crossing',
  'mapIssue.tooClose': 'too close to {place} ({d} m, needs at least {gap} m)',
  'mapIssue.unsupportedFormat': 'unsupported format {got}, expected 1',
  'mapIssue.expectedName': 'expected a non-empty string',
  'mapIssue.sizeOutOfRange': 'must be within {min}..{max} m, got {got}',
  'mapIssue.centerPointNotOrigin': 'the centre point must be at [0, 0]',
  'mapIssue.error': '{text}',
  'mapIssue.mapUnreadable': 'the map could not be read: the data is damaged',
  'mapIssue.notJson': 'the file is not JSON',
  'mapIssue.fileNotJson': '{file} is not JSON',
  'mapIssue.fileTooLarge': 'the file is over {kb} KB — this is no map',
  'mapIssue.storageUnavailable': 'browser storage is unavailable (private mode or blocked for this site): maps are not saved',
  'mapIssue.storageFull': 'browser storage is full: delete maps you no longer need or export them to files',
  'mapIssue.storageWriteFailed': 'could not write to browser storage: {error}',
  'mapIssue.nameEmpty': 'enter a name for the map',
  'mapIssue.nameSpaces': 'the name must not begin or end with a space',
  'mapIssue.nameTooLong': 'the name is longer than {max} characters',
  'mapIssue.fileNameInvalid': 'file name: lowercase Latin letters, digits and hyphens (up to 40)',
  'mapIssue.nameTaken': 'a map named “{name}” already exists',
  'mapIssue.notFound': 'there is no map named “{name}”',
  'mapIssue.devServerDown': 'the dev server is unreachable: {error}',
  'mapIssue.serverError': 'server error, HTTP {status}',
  'mapIssue.noTemplate': 'there is no template {name}',
  'mapPlace.base': 'base',
  'mapPlace.baseMirror': 'the mirrored base',
  'mapPlace.pointsSide': 'points.side',
  'mapPlace.pointsCenter': 'points.center',
  'mapPlace.pointsSideMirror': 'the mirrored side point',
  'mapPlace.denPos': 'den.pos',
  'mapPlace.denMirror': 'the mirrored den',

  /* ---------------- common, front screens, settings ---------------- */
  'common.back': 'Back',
  'common.next': 'Next',
  'common.loading': 'Loading…',
  'player.nameless': 'Player',
  'front.tagline': '2×2 · a fight for three points · {n} {n|hero|heroes}',
  'front.pressAnyKey': 'Press any key',
  'menu.aria': 'Main menu',
  'menu.online': 'Play online',
  'menu.bots': 'Play against bots',
  'menu.editor': 'Map editor',
  'menu.replays': 'My replays',
  'menu.heroes': 'Heroes / Artifacts',
  'menu.settings': 'Settings',
  'menu.you': 'You are {name}',
  'menu.noName': 'No name yet — we will ask before you play online',
  'settings.title': 'Settings',
  'settings.name': 'Player name',
  'settings.lang': 'Language',
  'settings.sound': 'Sound',
  'settings.on': 'On',
  'settings.off': 'Off',
  'settings.volume': 'Volume',
  'settings.quality': 'Graphics quality',
  'settings.quality.auto': 'Auto',
  'settings.quality.high': 'High',
  'settings.quality.medium': 'Medium',
  'settings.quality.low': 'Low',
  'settings.style': 'Art style',
  'settings.style.engrave': 'Original',
  'settings.style.toon': 'Cartoon',
  'settings.fog': 'Fog of war',
  'settings.fog.color': 'Coloured',
  'settings.fog.mono': 'Two-tone',
  'settings.hints': 'Control hints',
  'settings.hints.show': 'Show',
  'settings.hints.hide': 'Hide',
  'settings.lockCursor': 'Keep cursor in window',
  'nameEntry.title': 'What is your name?',
  'nameEntry.note': 'Other players in the room will see it.',
  'nameEntry.empty': 'Enter a name',

  /* ---------------- static page (index.html), plate and slot words drawn by CSS ---------------- */
  'stage.aria': '2×2 match',
  'legend.move': '<kbd>LMB</kbd>/<kbd>RMB</kbd> <b>on the ground</b> — walk, <b>on a foe</b> — strike, <b>on an ally</b> — keep close',
  'legend.skills': '<kbd>Q</kbd> <kbd>W</kbd> skills: aimed ones — <kbd>LMB</kbd> on the target (the Alchemist\'s shot — hold <kbd>LMB</kbd>), <kbd>RMB</kbd>/<kbd>Esc</kbd> cancel',
  'legend.shop': 'artifacts — in the shop at your base: click the counter or press <kbd>B</kbd> to open it, selling returns half',
  'legend.zoom': 'wheel — zoom',
  'legend.minimap': '<kbd>RMB</kbd> on the minimap — walk there',
  'legend.mute': '<kbd>M</kbd> sound on/off',
  'plate.charm': ' · charmed',
  'plate.fear': ' · afraid',
  'plate.stun': ' · stunned',
  'plate.brace': ' · braced',
  'slot.passive': 'passive',

  /* ---------------- main wiring, replay errors ---------------- */
  'main.webglTitle': 'Could not start the graphics',
  'main.webglText': 'The browser could not start WebGL. Turn on hardware acceleration and reload the page.',
  'main.rematch': 'Another match',
  'main.fromEditorMap': '{name} (from the editor)',
  'main.fromEditorGroup': 'From the editor',
  'main.mapBroken': 'This map will not open',
  'main.editorFailTitle': 'Could not start the editor',
  'main.editorFailText': 'The editor needs WebGL graphics. Turn on hardware acceleration and reload the page.',
  'main.replayTitle': 'Match replay',
  'main.replayFailed': 'The replay would not open',
  'replayError.notFound': 'Replay not found or deleted — recordings are kept for 7 days.',
  'replayError.unavailable': 'This match\'s replay is unavailable — the recording could not be saved.',
  'replayError.network': 'Could not reach the server. Check your connection and try again.',
  'replayError.busy': 'The server is busy right now. Try again in a minute.',
  'replayError.corrupt': 'The replay file is damaged — it cannot be read.',
  'replayError.list': 'Could not load the list of replays. Try again.',
  'replayError.outdated': 'Recorded in an older version of the game and can no longer be played',

  /* ---------------- hero select, hero marks ---------------- */
  'common.cooldown': 'cooldown {cd} s',
  'common.passiveLine': 'Passive: {name} — {desc}',
  'hs.title': 'Choose a hero',
  'hs.confirm': 'To battle',
  'hs.map': 'Map:',
  'hs.hp': 'Health',
  'hs.speed': 'Speed',
  'hs.range': 'Attack range',
  'hs.dmg': 'Damage',
  'icon.melee': 'Melee',
  'icon.ranged': 'Ranged',
  'icon.summon': 'Creatures',
  'icon.dash': 'Dash',
  'icon.stun': 'Stun',
  'icon.aoe': 'Area damage',
  'icon.zone': 'Ground zone',
  'icon.heal': 'Healing',
  'icon.shield': 'Shield',
  'icon.reveal': 'Sight',
  'icon.control': 'Control',
  'icon.empower': 'Empowerment',

  /* ---------------- shop ---------------- */
  'shop.notInBase': 'you must be at your base',
  'shop.noSlot': 'both slots are full — sell something',
  'shop.locked': 'locked for {s} s more',
  'shop.noInk': 'not enough ink ({cost} needed)',
  'shop.sell': 'Sell · +{n}',
  'shop.buy': 'Buy · {cost}',
  'shop.lockedShort': 'taken {s} s',
  'shop.needInk': 'need {cost}',
  'shop.noSlotShort': 'no slot',
  'shop.relic.ours': 'your team holds this relic',
  'shop.relic.theirs': 'the enemy holds it — knock it loose and it returns to the shop',
  'shop.relic.teamHasOne': 'your team already has a relic',
  'shop.relic.oursShort': 'Ours',
  'shop.relic.theirsShort': 'Theirs',
  'shop.relic.teamHasOneShort': 'have one',
  'shop.relic.auto': 'auto',
  'shop.relicsLabel': 'Relics — one per team, {cost} ink, held by both at once; if either of you dies, it is lost',
  'shop.passiveLabel': 'Passive — work on their own',
  'shop.activeLabel': 'Active — on E or R',

  /* ---------------- skill and slot tooltips ---------------- */
  'target.instant': 'instant',
  'target.direction': 'in a direction',
  'target.point': 'at a spot',
  'target.charge': 'charge — hold and release',
  'target.ally': 'on an ally',
  'target.enemy': 'on a foe',
  'tip.emptySlot': 'Empty — buy an artifact in the shop at your base',
  'tip.empty': 'Empty',
  'tip.vialCharge': 'Charge: {pct}% (max {max}%, +{rate}% per second)',
  'tip.worksItself': 'works on its own',
  'tip.noRelic': 'No relic — buy one in the shop at your base for {cost}',
  'tip.teamRelic': 'team relic',
  'tip.relicWorksItself': 'works on its own',
  'tip.sharedCooldown': 'cooldown {cd} s, shared by both',
  'tip.relicLost': 'If either of you dies, the relic is lost and returns to the shop',
  'tip.cdLeft': '{s} s left',

  /* ---------------- codex: heroes / artifacts guide ---------------- */
  'codex.heroes': 'Heroes',
  'codex.artifacts': 'Artifacts',
  'guide.passive': 'Passive',
  'guide.active': 'Active',
  'guide.relics': 'Relics',
  'guide.fullCharge': 'full charge in {cd} s',
  'guide.typeActive': 'active (E/R key)',
  'guide.typePassive': 'passive',
  'guide.noteBuy': 'Bought at your base for {cost} ink from the team\'s shared store',
  'guide.noteActive': 'Takes the E or R slot — press that key to use it',
  'guide.notePassive': 'Takes the E or R slot and works on its own — nothing to press',
  'guide.noteSell': 'Selling it at your base returns {n}',
  'guide.typeRelic': 'relic ({key}, one per match)',
  'guide.relicNote1': 'One per match: while one team holds it, the other cannot buy it',
  'guide.relicNote2': 'Bought at your base for {cost} ink and goes to both of you at once; a team holds only one relic',
  'guide.relicNote3': 'The death of either bearer knocks it loose — it returns to the shop',
  'guide.relicNote4': 'Cannot be sold',
  'guide.price': '{n} ink',
  'guide.cost': 'Price',
  'guide.sale': 'Sale',

  /* ---------------- own-hero stats block, status chips ---------------- */
  'stats.base': 'base {n}',
  'stats.dmg': 'Damage',
  'stats.aps': 'Attacks/s',
  'stats.range': 'Range, m',
  'stats.speed': 'Run speed, m/s',
  'stats.haste': 'haste',
  'stats.slow': 'slow',
  'stats.charging': 'charging',
  'stats.ford': 'ford',
  'status.stun': 'stun',
  'status.charm': 'charm',
  'status.fear': 'fear',
  'status.delusion': 'delusion',
  'status.sleepless': 'sleeplessness',
  'status.stealth': 'unseen',
  'status.silhouette': 'silhouette',
  'status.shield': 'shield',

  /* ---------------- in-match HUD ---------------- */
  'common.toMenu': 'To menu',
  'buff.hp': '{pct} base health',
  'buff.moveAndAttack': '{pct} run and attack speed',
  'buff.move': '{pct} run speed',
  'buff.attack': '{pct} attack speed',
  'buff.ours': 'Ours',
  'buff.theirs': 'Foes',
  'silence.left': ' — {s} s left',
  'silence.charm': 'Charmed: you walk to Sin — skills and artifacts are out of reach{left}',
  'silence.fear': 'Afraid: you flee to your base — skills and artifacts are out of reach{left}',
  'relicFeed.bought': '{team} bought “{relic}”',
  'relicFeed.lost': '{team} lost the relic “{relic}”',
  'relic.martyrShield.short': 'Shield',
  'relic.wildHorn.short': 'Horn',
  'relic.bestiaryEye.short': 'Eye',
  'relic.hydraHeart.short': 'Heart',
  'slot.noRelic': 'no relic',
  'slot.empty': 'empty',
  'slot.inShop': 'in the shop',
  'tag.unseen': 'unseen',
  'toast.allySold': 'Ally sold: {name} (+{n})',
  'toast.allyBought': 'Ally bought: {name} (−{n})',
  'banner.tallying': ' · tallying the results…',
  'banner.won': '{team} wins',
  'hud.inkTitle': 'The team\'s shared store: either of you spends it in the shop',
  'hud.ink': 'Team ink',
  'hud.stallHint': 'Shop open — <b>B</b> or click the counter',
  'hud.shopCloseAria': 'Close the shop',
  'hud.shopCloseTitle': 'Close (Esc)',
  'hud.healBlocked': '{name}: healing blocked',
  'hud.minimapTitle': 'RMB — walk here',
  'hud.miss': 'miss',
  'hud.overtime': 'overtime',
  'status.respawn': 'Respawn in {s} s',
  'status.line.charm': 'Charmed: you walk to Sin, orders do not take',
  'status.line.fear': 'Afraid: you flee to your base, orders do not take',
  'status.line.delusion': 'Delusion: friend and foe are confused, an attack may strike an ally',
  'status.line.meld': 'Meld: foes cannot see you while you stay in cover',
  'status.line.silhouette': 'Silhouette: you are seen, but cannot be touched while you stand still',
  'status.line.grass': 'In the grass: foes cannot see you until they come close',

  /* ---------------- match results ---------------- */
  'medal.damage': 'Most damage',
  'medal.damageValue': '{n} damage',
  'medal.capture': 'Chief conqueror',
  'medal.captureValue': '{n} capt. · {time}',
  'medal.undying': 'Unkillable',
  'medal.undyingValue': '{n} deaths · {taken} taken',
  'medal.hunter': 'Beast hunter',
  'medal.hunterValue': '{n} beasts',
  'medal.healer': 'Healer',
  'medal.healerValue': '{n} healed',
  'res.passiveTip': 'Passive “{name}”: {stat}',
  'res.passiveStat.rhino': '{v} m of shoving shrugged off',
  'res.passiveStat.durer': '{v} damage spared his allies',
  'res.passiveStat.landsknecht': '{v} damage by the second blow',
  'res.passiveStat.arquebusier': '{v} attacks at extra range',
  'res.passiveStat.astrologer': '{v} s of foes laid bare',
  'res.passiveStat.salamander': '{v} damage from the afterburn',
  'res.passiveStat.death': '{v} extra damage from the bonus',
  'res.passiveStat.ferryman': '{v} s of slow laid on',
  'res.passiveStat.devil': '{v} hp healed by the debt',
  'res.passiveStat.plague': '{v} extra healing and shield',
  'res.passiveStat.monk': '{v} s of stun/slow/charm/fear prevented',
  'res.passiveStat.grotesque': '{v} damage by thorns',
  'res.passiveStat.shadow': '{v} s of reveal avoided',
  'res.passiveStat.sin': '{v} extra points for kills',
  'res.passiveStat.nightmare': '{v} hp of enemy healing denied',
  'res.passiveStat.birdcatcher': '{v} s of foes in the magpies\' sight',
  'res.passiveStat.puppeteer': '{v} shield from smashed golems',
  'res.passiveStat.shewolf': '{v} damage turned by the pack',
  'res.bot': 'Bot',
  'res.supportTitle': 'healing {heal} · shields {shield}',
  'res.relics': 'Relics:',
  'res.relicLost': ' (lost)',
  'res.teamWon': 'victory',
  'res.col.hero': 'Hero',
  'res.col.kdaTitle': 'Kills / deaths / assists',
  'res.col.kda': 'K / D / A',
  'res.col.dmg': 'Damage to heroes',
  'res.col.taken': 'Taken',
  'res.col.heal': 'Healing',
  'res.col.monsters': 'Damage to beasts',
  'res.col.captures': 'Captures',
  'res.col.onPoints': 'On points',
  'res.col.inkTitle': 'Ink from the team\'s shared store: purchases minus sale refunds',
  'res.col.ink': 'Ink spent',
  'res.total': 'Total',
  'res.victory': 'Victory',
  'res.defeat': 'Defeat',
  'res.duration': 'The match lasted {time}',

  /* ---------------- casting toasts, radar, countdown, match, map picker ---------------- */
  'cast.pickFoe': 'choose a foe',
  'cast.monsterReturning': 'the beast cannot be hurt while it returns',
  'cast.tooFar': 'the target is beyond {range} m',
  'toast.named': '{name}: {text}',
  'toast.noRelic': 'T: your team has no relic — buy one in the shop at your base',
  'toast.relicPassive': '{name}: works on its own, nothing to press',
  'toast.artifactPassive': '{name}: works on its own, nothing to press',
  'toast.cooling': '{name}: cooldown {s} s',
  'toast.emptyArtifactSlot': '{key}: the slot is empty — artifacts are bought in the shop at your base',
  'toast.emptySkillSlot': '{key}: the slot is empty — skills are bought in the shop at your base',
  'toast.vialEmpty': '{name}: still empty',
  'axis.dmg': 'Damage',
  'axis.hp': 'Toughness',
  'axis.mobility': 'Mobility',
  'axis.range': 'Range',
  'axis.control': 'Control',
  'axis.support': 'Support',
  'radar.empty': 'The team is not yet assembled.',
  'radar.summary': 'Strong: {strong} · weak: {weak}',
  'countdown.go': 'Fight!',
  'match.soundOff': 'Sound: off',
  'match.soundOn': 'Sound: on',
  'maps.baked': '{name} (main)',
  'maps.builtin': 'Built-in',
  'maps.mine': 'My maps',

  /* ---------------- server error codes (shared/protocol.ts ErrorCode) ---------------- */
  'error.BAD_MESSAGE': 'The server did not understand the request — try again.',
  'error.HELLO_REQUIRED': 'The connection is not ready — try again in a second.',
  'error.PROTOCOL_MISMATCH': 'This version of the game is out of date — reload the page.',
  'error.MAP_MISMATCH': 'The server runs a different map — reload the page.',
  'error.ROOM_NOT_FOUND': 'The room was not found or is already playing.',
  'error.ROOM_FULL': 'The room has no free places left.',
  'error.TOO_MANY_ROOMS': 'The server is full right now — try later.',
  'error.NOT_IN_ROOM': 'You are not in a room yet.',
  'error.ALREADY_IN_ROOM': 'You are already in a room — leave it first.',
  'error.SEAT_INVALID': 'There is no such place.',
  'error.SEAT_TAKEN': 'That place is already taken.',
  'error.SEAT_NOT_YOURS': 'That place is not yours.',
  'error.TEAM_FULL': 'This team already has two players.',
  'error.HERO_INVALID': 'There is no such hero.',
  'error.HERO_TAKEN': 'That hero is already taken.',
  'error.HERO_REQUIRED': 'Choose a hero first.',
  'error.MATCH_ALREADY_STARTED': 'The match has already begun.',
  'error.MATCH_NOT_ACTIVE': 'The match has not begun yet.',
  'error.RATE_LIMITED': 'Too many requests — wait a little.',
  'error.SESSION_TAKEN_ELSEWHERE': 'This session is open in another tab or window.',
  'error.unknown': 'Server error: {code}',

  /* ---------------- online: lobby, connection ---------------- */
  'common.linkCopied': 'Link copied',
  'lobby.closed': 'The room was closed.',
  'lobby.heroTaken': 'taken',
  'lobby.room': '{name}\'s room',
  'lobby.rooms': 'Rooms',
  'lobby.create': 'Create a room',
  'lobby.seats': '{n} / 4 places',
  'lobby.join': 'Join',
  'lobby.emptyTitle': 'No one is playing yet',
  'lobby.emptySub': 'Be the first — create a room and call your friends.',
  'lobby.ready': 'Ready',
  'lobby.free': 'Free',
  'lobby.sit': 'Take',
  'lobby.unbot': 'Remove bot',
  'lobby.changeHero': 'Change hero',
  'lobby.pickHero': 'Choose hero',
  'lobby.disconnected': ' · disconnected',
  'lobby.copyLink': 'Copy a link for a friend',
  'lobby.linkAria': 'Link to the room',
  'lobby.leave': 'Leave the room',
  'net.reload': 'Reload',
  'net.versionTitle': 'A new version is available',
  'net.versionText': 'Your page is out of date and cannot connect to the server. Reload the page.',
  'net.mapTitle': 'The map has changed',
  'net.mapText': 'The server runs a different map than your page. Reload the page to load the new one.',
  'net.sessionTitle': 'The session is open in another tab',
  'net.sessionText': 'This account is in use in another tab or window. Reload the page to start over here.',
  'net.toLobby': 'To the lobby',
  'net.watchReplay': 'Watch the replay',

  /* ---------------- replays ---------------- */
  'replay.aborted': 'Match aborted',
  'replay.teamWon': '{team} won',
  'replay.bot': 'bot',
  'replay.empty': 'No replays yet. Play a match online — its recording will appear here and is kept for 7 days.',
  'replay.visionAll': 'All',
  'replay.follow': 'Follow this hero',
  'replay.again': 'Watch again',
  'replay.exit': 'Exit',
  'replay.controls': 'Replay controls',
  'replay.time': 'Replay time',
  'replay.playTitle': 'Pause / play (Space)',
  'replay.pause': 'Pause',
  'replay.play': 'Play',
  'replay.speed': 'Speed',
  'replay.vision': 'Vision',
  'replay.copyLink': 'Copy link',
  'replay.respawn': 'respawn {s} s',
  'replay.end': 'End of the recording',
  'replay.endWinner': '{score} · {team} won',

  /* ---------------- map editor ---------------- */
  'editor.prop.haystack': 'Haystack',
  'editor.prop.cart': 'Cart',
  'editor.prop.cross': 'Cross',
  'editor.prop.well': 'Well',
  'editor.wall.stone': 'Stone',
  'editor.wall.palisade': 'Palisade',
  'editor.wall.hedge': 'Hedge',
  'editor.building.house': 'House',
  'editor.building.tower': 'Tower',
  'editor.building.chapel': 'Chapel',
  'editor.building.mill': 'Mill',
  'editor.decor.bush': 'Bush',
  'editor.decor.grave': 'Grave',
  'editor.decor.post': 'Post',
  'editor.decor.barrel': 'Barrel',
  'editor.road.tract': 'Highroad',
  'editor.road.dirt': 'Dirt road',
  'editor.road.trail': 'Forest trail',
  'editor.group.edit': 'Edit',
  'editor.group.required': 'Required',
  'editor.group.points': 'Points',
  'editor.group.shapes': 'Shapes',
  'editor.group.props': 'Obstacles',
  'editor.group.lines': 'Lines',
  'editor.tool.select': 'Select / move',
  'editor.tool.base': 'Base',
  'editor.tool.side': 'Side point A',
  'editor.tool.den': 'Den',
  'editor.tool.trees': 'Tree',
  'editor.tool.rocks': 'Rock',
  'editor.tool.pillars': 'Pillar',
  'editor.tool.camps': 'Camp',
  'editor.tool.bridges': 'Bridge',
  'editor.tool.buildings': 'Building',
  'editor.tool.brush': 'Grass',
  'editor.tool.decor': 'Decor',
  'editor.tool.fields': 'Field',
  'editor.tool.fords': 'Ford',
  'editor.tool.walls': 'Wall / fence',
  'editor.tool.streams': 'Stream',
  'editor.tool.roads': 'Road',
  'editor.tool.cliffs': 'Ledge',
  'editor.tool.ponds': 'Pond',
  'editor.label.side': 'Point A',
  'editor.label.walls': 'Wall',
  'editor.label.props': 'Obstacle',
  'editor.untitled': 'Untitled',
  'editor.toolSettings': 'Tool settings',
  'editor.style': 'Style',
  'editor.type': 'Type',
  'editor.width': 'Width',
  'editor.closed': 'Closed area',
  'editor.hint.select': 'Click — select, drag — move. Empty ground — pan. ? — help.',
  'editor.hint.base': 'Click — place team A\'s base (its mirror is team B\'s base). The diagonal follows it.',
  'editor.hint.side': 'Click — place point A (mirrored into point C).',
  'editor.hint.den': 'Click — place the den; its mirror is the other beast\'s den.',
  'editor.hint.bridges': 'Click on a stream (within 1 m of it).',
  'editor.hint.fords': 'Ford: click on a stream — it will lie across the nearest one. Within the ford the stream can be crossed, but running is 20% slower.',
  'editor.hint.props': '{name}: click — place, R — rotate. Esc — back to selection.',
  'editor.hint.shape': '{name}: click — add a point. Enter or double click — finish, Backspace — remove the last one, Esc — cancel.',
  'editor.hint.place': '{name}: click — place. Esc — back to selection.',
  'editor.notSaved': 'Not saved',
  'editor.outOfRange': '{field} must be within {min}..{max}',
  'editor.nothingSelected': 'Nothing selected.',
  'editor.setsDiagonal': 'sets the diagonal',
  'editor.mirrored': 'mirrored',
  'editor.partCenter': 'centre (not mirrored)',
  'editor.partHalf': 'half',
  'editor.w': 'Width (w)',
  'editor.d': 'Depth (d)',
  'editor.rx': 'Radius x',
  'editor.rz': 'Radius z',
  'editor.scale': 'Scale',
  'editor.rotation': 'Rotation °',
  'editor.vertices': '{n} {n|point|points}{selected}. Drag a square to move a point; Alt+click an edge — add a point, Alt+click a point — remove it.',
  'editor.vertexSelected': ', point {i} selected',
  'editor.vertex': 'Point {i}',
  'editor.duplicate': 'Duplicate',
  'editor.delete': 'Delete',
  'editor.toHalf': 'Back to the half',
  'editor.toCenter': 'Make it a centre object',
  'editor.centerTitle': 'Centre objects are stored once, without a mirror, and must be symmetric as a set: a centre object off the origin keeps its mirror twin beside it.',
  'editor.noProblems': 'No problems: the map can be saved and played.',
  'editor.issueRoot': '(map)',
  'editor.more': '… {n} more',
  'editor.renderError': 'render error: {error}',
  'editor.rebuilt': 'rebuilt in {ms} ms',
  'editor.unsavedParen': '(not saved)',
  'editor.changed': ' — changed',
  'editor.sizeStatus': '{w} × {h} m',
  'editor.snapOn': 'snap {step} m',
  'editor.snapOff': 'snap off',
  'editor.noErrors': 'no errors',
  'editor.problemsCount': '{n} {n|problem|problems}',
  'editor.docTitle': '{mark}{name} — Map editor',
  'editor.newMap': 'new map',
  'editor.requiredNoDelete': 'The base, point A and the den are required: move them, do not delete them.',
  'editor.minVertices': 'Already the fewest points possible.',
  'editor.needPoints': 'At least {n} {n|point is|points are} needed.',
  'editor.fordNeedsStream': 'A ford lies across a stream: the map has no stream.',
  'editor.nothingToUndo': 'Nothing to undo.',
  'editor.nothingToRedo': 'Nothing to redo.',
  'editor.noneYet': '— none yet —',
  'editor.activeMap': '{name} (active)',
  'editor.confirmDiscard': 'Discard unsaved changes?',
  'editor.seeProblems': '{problems} (see “Problems”)',
  'editor.openFailed': 'Could not open {name}: {reason}',
  'editor.opened': 'Opened: {name}',
  'editor.notSavedReason': 'Not saved: {reason}.',
  'editor.confirmOverwrite': '{name} already exists. Overwrite it with this map?',
  'editor.saved': 'Saved: {name}',
  'editor.promptFile': 'File name in maps/ (lowercase Latin letters, digits, hyphens):',
  'editor.promptName': 'Save the map under the name:',
  'editor.confirmLeave': 'The map has unsaved changes: they will be lost when the page reloads (after a match, for instance). Leave for the menu anyway?',
  'editor.cannotPlay': 'This map cannot be played: {problems} (see “Problems”).',
  'editor.confirmExport': 'The map has {problems}: the game will not open such a file until they are fixed. Export anyway?',
  'editor.importFailedHead': 'File {file} was not imported:',
  'editor.importFailed': 'Not imported: {reason}.',
  'editor.imported': 'Imported from {file}: save the map (“Save as…”).',
  'editor.promptRename': 'New name for {name}:',
  'editor.renameFailed': 'Not renamed: {reason}.',
  'editor.renamed': 'Renamed: {name}',
  'editor.confirmRemove': 'Delete the map {name}? This cannot be undone.',
  'editor.removeFailed': 'Not deleted: {reason}.',
  'editor.removed': 'Deleted: {name}',
  'editor.templateFailed': 'Could not open: {reason}',
  'editor.templateOpened': 'The built-in map is open as a template: “Save as…” keeps a copy.',
  'editor.confirmNew': 'Create a new blank map {w} × {h} m?{lost}',
  'editor.changesLost': ' Unsaved changes will be lost.',
  'editor.activeFailed': 'Could not make it active: {reason}',
  'editor.nowActive': '{name} is now the active map: the dev server is rebuilding the game, reload the page.',
  'editor.devMaps': 'Maps in maps/',
  'editor.quoted': '“{name}”',
  'editor.draftBar': 'Enter or double click — finish · Backspace — remove a point · Esc — cancel',
  'editor.done': 'Done',
  'editor.cancel': 'Cancel',
  'editor.back': '← To menu',
  'editor.helpBtn': 'Help (?)',
  'editor.map': 'Map',
  'editor.name': 'Name',
  'editor.size': 'Size (m)',
  'editor.pointRadius': 'Point radius',
  'editor.save': 'Save',
  'editor.saveAs': 'Save as…',
  'editor.play': 'Play on this map',
  'editor.makeActive': 'Make active',
  'editor.newBlank': 'New blank map…',
  'editor.export': 'Export to file',
  'editor.import': 'Import from file',
  'editor.mapLabel': 'Map',
  'editor.open': 'Open',
  'editor.rename': 'Rename…',
  'editor.remove': 'Delete…',
  'editor.builtin': 'Built-in maps',
  'editor.builtinNote': 'Read-only: open one as a template and keep a copy with “Save as…”.',
  'editor.selection': 'Selection',
  'editor.viewTitle': 'View',
  'editor.snap': 'Snap to grid',
  'editor.metres': 'm',
  'editor.ovIssues': 'Map problems',
  'editor.ovNav': 'Walkability',
  'editor.ovSight': 'Blocks sight',
  'editor.problems': 'Problems',
  'editor.help': '<h2>Map editor</h2><p style="margin:0 0 8px;color:var(--muted)">You edit the red half; the blue one is its live mirror turned 180°. Placing an object on the blue half puts it at the mirrored spot.</p><table><tr><td>Left button + drag</td><td>move the object under the cursor (on empty ground — pan)</td></tr><tr><td>Right / middle button + drag</td><td>pan</td></tr><tr><td>Wheel</td><td>zoom from the cursor</td></tr><tr><td>Alt + wheel, R / Shift+R</td><td>rotate the selection (5° / 15°)</td></tr><tr><td>[ ]</td><td>shrink / grow the selection (size, width, scale)</td></tr><tr><td>Blue squares</td><td>drag to resize a building (w/d) or grass (rx/rz)</td></tr><tr><td>Drag a vertex</td><td>reshape a line or polygon</td></tr><tr><td>Alt + click an edge / double click an edge</td><td>add a vertex</td></tr><tr><td>Alt + click a vertex</td><td>remove that vertex</td></tr><tr><td>Del / Backspace</td><td>delete the selected vertex or object</td></tr><tr><td>Ctrl+D</td><td>duplicate</td></tr><tr><td>Ctrl+Z / Ctrl+Shift+Z</td><td>undo / redo</td></tr><tr><td>Ctrl+S</td><td>save</td></tr><tr><td>Click (line tools)</td><td>add a point; Enter or double click finishes, Backspace removes the last, Esc cancels</td></tr><tr><td>V / Esc</td><td>selection tool / cancel; Esc with nothing selected — to the menu</td></tr><tr><td>G</td><td>snap to grid on/off</td></tr><tr><td>F</td><td>show the whole map</td></tr><tr><td>?</td><td>this help</td></tr></table>',
};
